import { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useLinkRequest, useLinkTargets } from '@/queries/clientMeetings'
import type { CallRequest, LinkTarget, RequestLinkKind } from '@/types/clientMeetings'
import { stateLabel } from './requestLabels'

function TargetGroup({ heading, targets, disabled, onPick }: { heading: string; targets: LinkTarget[]; disabled: boolean; onPick: (target: LinkTarget) => void }) {
  if (!targets.length) return null
  return (
    <div>
      <div className="px-1 pb-1 pt-3 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">{heading}</div>
      <ul className="divide-y divide-etn-border-row rounded-etn-card border border-etn-border-hairline">
        {targets.map(target => (
          <li key={target.id}>
            <button type="button" disabled={disabled} onClick={() => onPick(target)} className="block w-full px-3 py-2 text-left hover:bg-etn-row-hover disabled:cursor-wait disabled:opacity-60">
              <span className="text-[13px] font-medium text-etn-text">
                {target.ref} {target.title}
              </span>
              <span className="block text-[12px] text-etn-text-soft">{stateLabel(target.state)}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Pick the ticket or Linear issue a request is about, from this client's own.
export function ChangeLinkDialog({ request, companyId, open, onOpenChange }: { request: CallRequest; companyId: string; open: boolean; onOpenChange: (open: boolean) => void }) {
  const [search, setSearch] = useState('')
  const targets = useLinkTargets(companyId, open).data?.data
  const linkRequest = useLinkRequest()
  const matches = (target: LinkTarget) => `${target.ref} ${target.title}`.toLowerCase().includes(search.trim().toLowerCase())
  const pick = (kind: RequestLinkKind) => (target: LinkTarget) =>
    linkRequest.mutate({ requestId: request._id, kind, id: target.id }, { onSuccess: () => onOpenChange(false) })
  const tickets = (targets?.tickets ?? []).filter(matches)
  const issues = (targets?.issues ?? []).filter(matches)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{request.link ? 'Change link' : 'Link to a ticket'}</DialogTitle>
          <DialogDescription>{request.title}</DialogDescription>
        </DialogHeader>
        <Input aria-label="Search tickets and issues" placeholder="Search by ref or title" value={search} onChange={event => setSearch(event.target.value)} />
        <TargetGroup heading="Help desk tickets and tracked items" targets={tickets} disabled={linkRequest.isPending} onPick={pick('ticket')} />
        <TargetGroup heading="Linear issues" targets={issues} disabled={linkRequest.isPending} onPick={pick('linear')} />
        {targets && !tickets.length && !issues.length && <p className="py-4 text-center text-[13px] text-etn-text-soft">Nothing matches</p>}
      </DialogContent>
    </Dialog>
  )
}
